import { useState } from "react";
import { Maestro, Sparkles, Copy, Check } from "../shared/Icon";
import { Button } from "../shared/Button";
import { BufferIndicator } from "../shared/BufferIndicator";
import { MessageContent } from "./MessageContent";
import type { ChatMessage, TaskProposal, TrackProposal } from "../../lib/types";
import { cn } from "../../lib/cn";

/**
 * Visual bubble for a single chat turn. The right pane and the full-page
 * Chat view both render the same shape so the conversation reads the same
 * everywhere.
 */
export function Bubble({
  msg,
  onAccept,
  onAcceptTrack,
}: {
  msg: ChatMessage;
  onAccept: (dispatch: boolean) => void;
  onAcceptTrack: (dispatch: boolean) => void;
}) {
  const isUser = msg.role === "user";
  const time = formatTime(msg.timestamp);

  if (isUser) {
    return (
      <div className="px-1">
        <div className="text-caption text-text-tertiary mb-1.5 flex items-center gap-2">
          <span className="text-text-secondary font-medium">You</span>
          <span aria-hidden="true">·</span>
          <span>{time}</span>
        </div>
        <div className="text-body text-text-primary whitespace-pre-wrap break-words leading-relaxed">
          <MessageContent content={msg.content} plain />
        </div>
      </div>
    );
  }

  if (isCliUnavailableContent(msg.content)) {
    return <CliUnavailable />;
  }

  return (
    <div className="px-1">
      <div className="text-caption text-text-tertiary mb-1.5 flex items-center gap-2">
        <span className="inline-flex items-center gap-1.5">
          <span
            className={cn(
              "h-4 w-4 inline-flex items-center justify-center rounded-sm",
              "bg-accent-soft text-accent",
            )}
            aria-hidden="true"
          >
            <Maestro size={10} />
          </span>
          <span className="text-text-secondary font-medium">Maestro</span>
        </span>
        <span aria-hidden="true">·</span>
        <span>{time}</span>
      </div>
      <div className="text-body text-text-primary leading-relaxed">
        <MessageContent content={msg.content} />
        {msg.task_proposal && (
          <ProposalCard proposal={msg.task_proposal} onAccept={onAccept} />
        )}
        {msg.track_proposal && (
          <TrackProposalCard
            proposal={msg.track_proposal}
            onAccept={onAcceptTrack}
          />
        )}
      </div>
    </div>
  );
}

export function ThinkingBubble() {
  return (
    <div className="px-1">
      <div className="text-caption text-text-tertiary mb-1.5 flex items-center gap-2">
        <span className="inline-flex items-center gap-1.5">
          <span
            className={cn(
              "h-4 w-4 inline-flex items-center justify-center rounded-sm",
              "bg-accent-soft text-accent",
            )}
            aria-hidden="true"
          >
            <Maestro size={10} />
          </span>
          <span className="text-text-secondary font-medium">Maestro</span>
        </span>
      </div>
      <BufferIndicator size={18} label="Maestro is thinking" />
    </div>
  );
}

/** Assistant bubble that grows as streaming text arrives. */
export function StreamingBubble({ text }: { text: string }) {
  return (
    <div className="px-1">
      <div className="text-caption text-text-tertiary mb-1.5 flex items-center gap-2">
        <span className="inline-flex items-center gap-1.5">
          <span
            className={cn(
              "h-4 w-4 inline-flex items-center justify-center rounded-sm",
              "bg-accent-soft text-accent",
            )}
            aria-hidden="true"
          >
            <Maestro size={10} />
          </span>
          <span className="text-text-secondary font-medium">Maestro</span>
        </span>
      </div>
      {text ? (
        <div className="text-body text-text-primary leading-relaxed">
          <MessageContent content={text} />
        </div>
      ) : (
        <BufferIndicator size={18} label="Maestro is thinking" />
      )}
    </div>
  );
}

export function EmptyChat({ hint }: { hint?: string }) {
  return (
    <div className="flex-1 flex items-center justify-center py-12 px-6">
      <div className="text-center max-w-[360px]">
        <div className="inline-flex items-center justify-center h-9 w-9 rounded-md bg-accent-soft text-accent mb-3 mx-auto">
          <Sparkles />
        </div>
        <div className="text-body text-text-secondary leading-relaxed">
          {hint ??
            "Ask the managing agent about any registered project. It has read access to every synced doc."}
        </div>
      </div>
    </div>
  );
}

function ProposalCard({
  proposal,
  onAccept,
}: {
  proposal: TaskProposal;
  onAccept: (dispatch: boolean) => void;
}) {
  return (
    <div className="mt-3 border-l-2 border-accent pl-3 py-1">
      <div className="text-caption text-text-tertiary uppercase tracking-wide mb-1">
        Proposed task
      </div>
      <div className="text-body font-medium text-text-primary">
        {proposal.title}
      </div>
      {proposal.rationale && (
        <div className="text-small text-text-secondary mt-1 leading-relaxed">
          {proposal.rationale}
        </div>
      )}
      <details className="mt-2 text-text-tertiary">
        <summary className="cursor-pointer text-small text-text-tertiary hover:text-text-secondary">
          prompt
        </summary>
        <pre className="font-mono text-[12.5px] whitespace-pre-wrap mt-1 text-text-secondary leading-relaxed">
          {proposal.prompt}
        </pre>
      </details>
      <div className="flex items-center gap-2 mt-3">
        <Button size="sm" variant="secondary" onClick={() => onAccept(false)}>
          Add to board
        </Button>
        <Button size="sm" variant="primary" onClick={() => onAccept(true)}>
          Accept &amp; dispatch
        </Button>
      </div>
    </div>
  );
}

function TrackProposalCard({
  proposal,
  onAccept,
}: {
  proposal: TrackProposal;
  onAccept: (dispatch: boolean) => void;
}) {
  const featureCount = proposal.plan.milestones.reduce(
    (n, m) => n + m.features.length,
    0,
  );
  return (
    <div className="mt-3 border-l-2 border-accent pl-3 py-1">
      <div className="text-caption text-text-tertiary uppercase tracking-wide mb-1">
        Proposed track
      </div>
      <div className="text-body font-medium text-text-primary">
        {proposal.title}
      </div>
      {proposal.description && (
        <div className="text-small text-text-secondary mt-1 leading-relaxed">
          {proposal.description}
        </div>
      )}
      <div className="text-mono text-text-tertiary mt-2">
        {proposal.plan.milestones.length} milestone
        {proposal.plan.milestones.length === 1 ? "" : "s"} · {featureCount} feature
        {featureCount === 1 ? "" : "s"} · autonomy {proposal.autonomy_level}
      </div>
      <details className="mt-2 text-text-tertiary">
        <summary className="cursor-pointer text-small text-text-tertiary hover:text-text-secondary">
          plan
        </summary>
        <ol className="font-mono text-[12.5px] mt-1 list-decimal list-inside space-y-1 text-text-secondary">
          {proposal.plan.milestones.map((m, i) => (
            <li key={i}>
              <span className="font-semibold">{m.title}</span>
              <ul className="ml-4 list-disc list-inside">
                {m.features.map((f, j) => (
                  <li key={j}>{f.title}</li>
                ))}
              </ul>
            </li>
          ))}
        </ol>
      </details>
      <div className="flex items-center gap-2 mt-3">
        <Button size="sm" variant="secondary" onClick={() => onAccept(false)}>
          Save plan
        </Button>
        <Button size="sm" variant="primary" onClick={() => onAccept(true)}>
          Save &amp; dispatch
        </Button>
      </div>
    </div>
  );
}

function CliUnavailable() {
  const [copied, setCopied] = useState(false);
  const cmd = "claude login";
  const copy = () => {
    void navigator.clipboard.writeText(cmd).then(() => {
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1200);
    });
  };
  return (
    <div className="px-1">
      <div className="text-caption text-text-tertiary mb-1.5">Maestro</div>
      <div className="text-body text-text-primary space-y-2">
        <div>
          The local <CodeChip>claude</CodeChip> CLI is not reachable.
        </div>
        <div className="text-small text-text-secondary">
          Authenticate it on the orchestrator host with{" "}
          <CodeChip>claude login</CodeChip> and try again.
        </div>
        <button
          onClick={copy}
          className={cn(
            "inline-flex items-center gap-1.5 h-7 px-2 rounded-sm",
            "bg-bg-inset text-mono text-text-secondary text-[12.5px]",
            "hover:text-text-primary hover:bg-accent-soft",
            "transition-colors duration-fast ease-out",
          )}
        >
          {copied ? <Check /> : <Copy />}
          <span>{copied ? "copied" : "Copy install command"}</span>
        </button>
      </div>
    </div>
  );
}

function CodeChip({ children }: { children: React.ReactNode }) {
  return (
    <code className="font-mono text-[12.5px] px-1 py-[1px] rounded-sm bg-bg-inset text-text-primary">
      {children}
    </code>
  );
}

function isCliUnavailableContent(c: string): boolean {
  const low = c.toLowerCase();
  return (
    low.includes("claude cli") &&
    (low.includes("not available") ||
      low.includes("unavailable") ||
      low.includes("not reachable") ||
      low.includes("not found"))
  );
}

export function isCliUnavailable(messages: ChatMessage[]): boolean {
  for (let i = messages.length - 1; i >= 0; i--) {
    if (messages[i].role === "assistant") {
      return isCliUnavailableContent(messages[i].content);
    }
  }
  return false;
}

function formatTime(iso: string): string {
  try {
    const d = new Date(iso);
    return `${d.getHours()}:${String(d.getMinutes()).padStart(2, "0")}`;
  } catch {
    return "";
  }
}
